import React, { createContext, useContext, ReactNode, useState, useCallback, useEffect } from 'react';
import { useLoadScript } from '@react-google-maps/api';
import { GOOGLE_MAPS_LIBRARIES } from '../utils/googleMapsUtils';

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError: Error | undefined;
  retry: () => void;
  /** Call this to trigger lazy-loading of the Google Maps script */
  requestLoad: () => void;
}

const GoogleMapsContext = createContext<GoogleMapsContextType>({
  isLoaded: false,
  loadError: undefined,
  retry: () => {},
  requestLoad: () => {}
});

export const useGoogleMaps = () => useContext(GoogleMapsContext);

interface GoogleMapsProviderProps {
  children: ReactNode;
}

// Composant interne qui utilise useLoadScript (monté seulement quand shouldLoad=true)
const GoogleMapsLoader: React.FC<{
  onError: (error: Error) => void;
  onSuccess: () => void;
}> = ({ onError, onSuccess }) => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  useEffect(() => {
    if (loadError) {
      console.error('[GoogleMaps] Erreur de chargement:', loadError.message);
      onError(loadError);
    }
  }, [loadError, onError]);

  useEffect(() => {
    if (isLoaded) {
      console.log('[GoogleMaps] API chargee avec succes');
      onSuccess();
    }
  }, [isLoaded, onSuccess]);

  return null;
};

export const GoogleMapsProvider: React.FC<GoogleMapsProviderProps> = ({ children }) => {
  const [shouldLoad, setShouldLoad] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [currentError, setCurrentError] = useState<Error | undefined>(undefined);
  const [isLoaded, setIsLoaded] = useState(false);

  const requestLoad = useCallback(() => {
    setShouldLoad(true);
  }, []);

  const handleError = useCallback((error: Error) => {
    setHasError(true);
    setCurrentError(error);
    setIsLoaded(false);
  }, []);

  const handleSuccess = useCallback(() => {
    setHasError(false);
    setCurrentError(undefined);
    setIsLoaded(true);
  }, []);

  const retry = useCallback(() => {
    console.log('[GoogleMaps] Retry demande...');

    // Nettoyer l'ancien script Google Maps s'il existe
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.remove();
    }

    // Reset le state global de @react-google-maps/api
    // @ts-ignore - accessing internal state
    if (window.google?.maps) {
      // @ts-ignore
      delete window.google.maps;
    }

    setHasError(false);
    setCurrentError(undefined);
    setIsLoaded(false);

    // Force un remount du loader avec une nouvelle clé
    setRetryKey(prev => prev + 1);
  }, []);

  // Auto-load en background 10s après le démarrage (non bloquant)
  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldLoad(true);
    }, 10000);
    return () => clearTimeout(timer);
  }, []);

  // Auto-retry après 5 secondes si erreur (max 2 tentatives)
  useEffect(() => {
    if (hasError && retryKey < 2) {
      const timer = setTimeout(() => {
        console.log('[GoogleMaps] Auto-retry apres erreur...');
        retry();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [hasError, retryKey, retry]);

  const value = {
    isLoaded,
    loadError: currentError,
    retry,
    requestLoad
  };

  return (
    <GoogleMapsContext.Provider value={value}>
      {shouldLoad && (
        <GoogleMapsLoader
          key={retryKey}
          onError={handleError}
          onSuccess={handleSuccess}
        />
      )}
      {children}
    </GoogleMapsContext.Provider>
  );
};
